/**
 * WysiwygEditor Component
 * A Word-like WYSIWYG editor using TipTap.
 * Works with markdown input/output for compatibility.
 */

import React, { useCallback, useEffect } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  ListChecks,
  Quote,
  Code,
  Link as LinkIcon,
  Minus,
  Undo,
  Redo,
} from 'lucide-react';
import { marked } from 'marked';
import TurndownService from 'turndown';

interface WysiwygEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
  onEditorReady?: (editor: Editor) => void;
  toolbarExtra?: React.ReactNode;
}

// Configure turndown for markdown conversion
const turndownService = new TurndownService({
  headingStyle: 'atx',
  bulletListMarker: '-',
  codeBlockStyle: 'fenced',
});

// Handle task list items (checkbox -> markdown)
turndownService.addRule('taskListItem', {
  filter: function (node) {
    return (
      node.nodeName === 'LI' &&
      node.getAttribute('data-type') === 'taskItem'
    );
  },
  replacement: function (content, node) {
    const isChecked = (node as HTMLElement).getAttribute('data-checked') === 'true';
    const checkbox = isChecked ? '- [x]' : '- [ ]';
    return checkbox + ' ' + content.trim() + '\n';
  },
});

// Configure marked for HTML conversion
marked.setOptions({
  breaks: true,
  gfm: true,
});

// Convert markdown to HTML
const markdownToHtml = (markdown: string): string => {
  return marked.parse(markdown) as string;
};

// Convert HTML to markdown
const htmlToMarkdown = (html: string): string => {
  return turndownService.turndown(html);
};

interface ToolbarButtonProps {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  onClick,
  isActive = false,
  disabled = false,
  title,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`
      p-2 rounded-lg transition-colors
      ${isActive
        ? 'bg-emerald-100 text-emerald-700'
        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200'
      }
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    `}
  >
    {children}
  </button>
);

const ToolbarDivider: React.FC = () => (
  <div className="w-px h-6 bg-slate-300 mx-1" />
);

export const WysiwygEditor: React.FC<WysiwygEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start typing...',
  className = '',
  minHeight = 'min-h-[400px]',
  onEditorReady,
  toolbarExtra,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 hover:text-blue-800 underline cursor-pointer',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      Underline,
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
    ],
    content: markdownToHtml(value),
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const markdown = htmlToMarkdown(html);
      onChange(markdown);
    },
    editorProps: {
      attributes: {
        class: `prose max-w-none focus:outline-none ${minHeight} p-4`,
      },
    },
  });

  // Notify parent when editor is ready
  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor);
    }
  }, [editor, onEditorReady]);

  // Update editor content when value prop changes externally
  useEffect(() => {
    if (editor && value) {
      const currentMarkdown = htmlToMarkdown(editor.getHTML());
      if (currentMarkdown !== value) {
        editor.commands.setContent(markdownToHtml(value));
      }
    }
  }, [editor, value]);

  const setLink = useCallback(() => {
    if (!editor) return;

    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    if (url === null) return;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) {
    return (
      <div className={`border border-slate-200 rounded-xl overflow-hidden ${className}`}>
        <div className="p-4 text-slate-400">Loading editor...</div>
      </div>
    );
  }

  return (
    <div className={`border border-slate-200 rounded-xl overflow-hidden bg-white ${className}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 bg-slate-50 border-b border-slate-200">
        {/* Undo/Redo */}
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo (Ctrl+Z)"
        >
          <Undo className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo (Ctrl+Y)"
        >
          <Redo className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarDivider />

        {/* Text formatting */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Bold (Ctrl+B)"
        >
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Italic (Ctrl+I)"
        >
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Underline (Ctrl+U)"
        >
          <UnderlineIcon className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          title="Strikethrough"
        >
          <Strikethrough className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCode().run()}
          isActive={editor.isActive('code')}
          title="Inline Code"
        >
          <Code className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarDivider />

        {/* Headings */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Heading 1"
        >
          <Heading1 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
        >
          <Heading2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
          title="Heading 3"
        >
          <Heading3 className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarDivider />

        {/* Lists */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleTaskList().run()}
          isActive={editor.isActive('taskList')}
          title="Checklist"
        >
          <ListChecks className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarDivider />

        {/* Block elements */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          title="Blockquote"
        >
          <Quote className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={setLink}
          isActive={editor.isActive('link')}
          title="Add Link"
        >
          <LinkIcon className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Horizontal Rule"
        >
          <Minus className="w-4 h-4" />
        </ToolbarButton>

        {toolbarExtra && (
          <>
            <ToolbarDivider />
            {toolbarExtra}
          </>
        )}
      </div>

      {/* Editor Content */}
      <EditorContent
        editor={editor}
        className="wysiwyg-editor"
      />

      {/* Footer */}
      <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
        <span className="hidden sm:inline">
          <kbd className="px-1.5 py-0.5 bg-slate-200 rounded text-xs">Ctrl+B</kbd> Bold
          <span className="mx-2">|</span>
          <kbd className="px-1.5 py-0.5 bg-slate-200 rounded text-xs">Ctrl+I</kbd> Italic
          <span className="mx-2">|</span>
          <kbd className="px-1.5 py-0.5 bg-slate-200 rounded text-xs">Ctrl+Z</kbd> Undo
        </span>
        <span className="sm:hidden">Word-like editor • Use toolbar for formatting</span>
      </div>

      {/* Editor styles */}
      <style>{`
        .wysiwyg-editor .ProseMirror {
          outline: none;
        }
        .wysiwyg-editor .ProseMirror p {
          margin: 0.5rem 0;
          color: #475569;
          line-height: 1.6;
        }
        .wysiwyg-editor .ProseMirror h1 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #0f172a;
          margin: 1.5rem 0 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid #e2e8f0;
        }
        .wysiwyg-editor .ProseMirror h2 {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1e293b;
          margin: 1.25rem 0 0.75rem;
        }
        .wysiwyg-editor .ProseMirror h3 {
          font-size: 1.125rem;
          font-weight: 600;
          color: #334155;
          margin: 1rem 0 0.5rem;
        }
        .wysiwyg-editor .ProseMirror ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .wysiwyg-editor .ProseMirror ol {
          list-style-type: decimal;
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .wysiwyg-editor .ProseMirror li {
          color: #475569;
          margin: 0.25rem 0;
        }
        .wysiwyg-editor .ProseMirror blockquote {
          border-left: 4px solid #60a5fa;
          padding-left: 1rem;
          margin: 1rem 0;
          background: #eff6ff;
          padding: 0.5rem 1rem;
          font-style: italic;
          color: #475569;
        }
        .wysiwyg-editor .ProseMirror code {
          background: #f1f5f9;
          padding: 0.125rem 0.375rem;
          border-radius: 0.25rem;
          font-family: monospace;
          font-size: 0.875rem;
          color: #334155;
        }
        .wysiwyg-editor .ProseMirror hr {
          border: none;
          border-top: 1px solid #e2e8f0;
          margin: 1.5rem 0;
        }
        .wysiwyg-editor .ProseMirror a {
          color: #2563eb;
          text-decoration: underline;
        }
        .wysiwyg-editor .ProseMirror a:hover {
          color: #1d4ed8;
        }
        .wysiwyg-editor .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #94a3b8;
          pointer-events: none;
          height: 0;
        }
        .wysiwyg-editor .ProseMirror ul[data-type="taskList"] {
          list-style: none;
          padding-left: 0;
        }
        .wysiwyg-editor .ProseMirror ul[data-type="taskList"] li {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
        }
        .wysiwyg-editor .ProseMirror ul[data-type="taskList"] li > label {
          flex: 0 0 auto;
          margin-top: 0.25rem;
        }
        .wysiwyg-editor .ProseMirror ul[data-type="taskList"] li > div {
          flex: 1 1 auto;
        }
        .wysiwyg-editor .ProseMirror ul[data-type="taskList"] li[data-checked="true"] > div {
          text-decoration: line-through;
          color: #94a3b8;
        }
      `}</style>
    </div>
  );
};

export { ToolbarButton, ToolbarDivider };
export type { Editor };
export default WysiwygEditor;
